/**
 * Phase 11B (`AIA-81`) display-name-then-email rule, applied consistently everywhere a person's
 * name renders in the SPA: show `Person.DisplayName` when it is a real value, otherwise fall back
 * to `Person.Email` — every bootstrap-created person (phase 11B's `PD-33`) starts with an empty
 * display name until an administrator sets one, and this is the one place that decides how such a
 * person is shown. Deliberately just this one rule, not the richer "show both, searchable by both"
 * list UI, which is a separate, later improvement.
 */

/** The minimum a caller needs in order to display a person's name. */
export interface DisplayNameable {
  displayName?: string | null;
  email: string;
}

/**
 * Formats a person for display: their display name when it is set, otherwise their email.
 *
 * @param person - The person to format, carrying at least a display name (which may be missing or blank) and an email.
 * @returns `person.displayName` when it is a non-empty, non-whitespace string; otherwise `person.email`.
 */
export function formatPersonName(person: DisplayNameable): string {
  const displayName = person.displayName;
  return displayName && displayName.trim().length > 0 ? displayName : person.email;
}
